import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { Breadcrumbs } from '../../components/shared/Breadcrumbs'

export default function AzureBackupPricingUkGuide() {
  return (
    <>
      <Helmet>
        <title>Azure Backup Pricing UK 2026 — Why Storage Beats the Instance Fee | AzureCalc.uk</title>
        <meta
          name="description"
          content="Azure Backup pricing explained for UK teams. The per-instance fee is small — vault storage is the real cost driver. LRS vs GRS, retention policy impact, and the Cross-Region Restore trap. All in GBP."
        />
        <link rel="canonical" href="https://www.azure-calc.co.uk/guides/azure-backup-pricing-uk" />
        <meta property="og:title" content="Azure Backup Pricing UK 2026 — Why Storage Beats the Instance Fee" />
        <meta property="og:url" content="https://www.azure-calc.co.uk/guides/azure-backup-pricing-uk" />
        <meta property="og:image" content="https://www.azure-calc.co.uk/og/azure-calc-og.png" />
        <meta property="og:type" content="article" />
      </Helmet>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <Breadcrumbs />

        <h1 className="text-2xl font-bold tracking-tight mt-4 mb-3" style={{ color: 'var(--text-primary)' }}>
          Azure Backup Pricing UK 2026
        </h1>
        <p className="text-[14px] mb-2" style={{ color: 'var(--text-tertiary)' }}>
          Protected instance fees · Vault storage · LRS / GRS / ZRS / RA-GRS · Retention policy impact
        </p>
        <p className="text-[12px] mb-8 italic" style={{ color: 'var(--text-tertiary)' }}>
          Built and verified by an independent Azure engineer. Prices in GBP, UK South, April 2026.
        </p>

        {/* Section 1 */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            1. Azure Backup pricing at a glance
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            Azure Backup has two billing dimensions: a protected instance fee charged per workload per month, and backup vault storage charged per GB per month. There is also an optional add-on for Cross-Region Restore, which requires RA-GRS vault storage. Most teams notice the per-instance fee first — it looks modest — and underestimate the storage line, which is usually the dominant cost.
          </p>

          <div className="rounded-lg overflow-hidden text-[13px] mb-4" style={{ border: '1px solid var(--outline-variant)' }}>
            <table className="w-full">
              <thead style={{ backgroundColor: 'var(--surface-container-low)' }}>
                <tr>
                  <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--text-primary)' }}>Workload</th>
                  <th className="text-right px-4 py-2 font-semibold" style={{ color: 'var(--text-primary)' }}>Instance fee (USD)</th>
                  <th className="text-right px-4 py-2 font-semibold" style={{ color: 'var(--text-primary)' }}>Instance fee (GBP)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Azure VM',              '$10.00/month', '£7.55/month'],
                  ['SQL Server in Azure VM','$31.25/month', '£23.59/month'],
                  ['Azure Files',           '$6.25/month',  '£4.72/month'],
                ].map(([workload, usd, gbp]) => (
                  <tr key={workload} style={{ borderTop: '1px solid var(--outline-variant)' }}>
                    <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>{workload}</td>
                    <td className="px-4 py-2 text-right" style={{ color: 'var(--text-tertiary)' }}>{usd}</td>
                    <td className="px-4 py-2 text-right" style={{ color: 'var(--text-primary)' }}>{gbp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-lg overflow-hidden text-[13px]" style={{ border: '1px solid var(--outline-variant)' }}>
            <table className="w-full">
              <thead style={{ backgroundColor: 'var(--surface-container-low)' }}>
                <tr>
                  <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--text-primary)' }}>Vault storage type</th>
                  <th className="text-right px-4 py-2 font-semibold" style={{ color: 'var(--text-primary)' }}>Rate (USD/GB/month)</th>
                  <th className="text-right px-4 py-2 font-semibold" style={{ color: 'var(--text-primary)' }}>Rate (GBP/GB/month)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Standard LRS',    '$0.028', '£0.0211'],
                  ['Standard ZRS',    '$0.035', '£0.0264'],
                  ['Standard GRS',    '$0.056', '£0.0423'],
                  ['Standard RA-GRS', '$0.0711','£0.0537'],
                ].map(([t, usd, gbp]) => (
                  <tr key={t} style={{ borderTop: '1px solid var(--outline-variant)' }}>
                    <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>{t}</td>
                    <td className="px-4 py-2 text-right" style={{ color: 'var(--text-tertiary)' }}>{usd}</td>
                    <td className="px-4 py-2 text-right" style={{ color: 'var(--text-primary)' }}>{gbp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[12px] mt-2" style={{ color: 'var(--text-tertiary)' }}>
            Source: Azure Retail Prices API (USD-only service), converted at 0.7548 GBP/USD. Verified April 2026.
          </p>
        </section>

        {/* Section 2 */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            2. Why storage usually dominates the instance fee
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            Consider 10 Azure VMs, each with 500 GB of active data and a 30-day retention policy. The instance fees total £75.50/month — just £7.55 per VM. That looks manageable. But the backup vault storage tells a different story. Azure Backup accumulates incremental backup points throughout the retention window. Assuming a 5% daily change rate (typical for general-purpose VMs), each VM accumulates roughly 500 GB × (1 + 0.05 × 30) = 2,250 GB in the vault. Across 10 VMs that's 22,500 GB. At LRS rates: 22,500 GB × £0.0211 = £474.75/month.
          </p>
          <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'rgba(232,176,0,0.08)', border: '1px solid rgba(232,176,0,0.3)' }}>
            <p className="text-[13px] font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Worked example: 10 VMs, 500 GB active data, 30-day retention, LRS</p>
            <div className="space-y-1 text-[13px]" style={{ color: 'var(--text-secondary)' }}>
              <div className="flex justify-between"><span>Instance fees (10 × £7.55)</span><span>£75.50</span></div>
              <div className="flex justify-between font-semibold" style={{ color: 'var(--text-primary)' }}><span>Vault storage (22,500 GB × £0.0211)</span><span>£474.75</span></div>
              <div className="flex justify-between pt-1" style={{ borderTop: '1px solid rgba(232,176,0,0.3)' }}><span>Total</span><span>£550.25/month</span></div>
              <p className="text-[11px] pt-1" style={{ color: 'var(--text-tertiary)' }}>Storage is 86% of the total. Instance fees are the smaller line item.</p>
            </div>
          </div>
          <p className="text-[14px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            The storage-to-instance ratio gets worse with longer retention and higher daily change rates (SQL Server at 10% daily change stores proportionally more). This is the headline insight for Azure Backup: plan your budget around the storage cost, not the instance fee.
          </p>
        </section>

        {/* Section 3 */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            3. LRS vs GRS vs ZRS vs RA-GRS — when does each make sense?
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            Vault storage redundancy is the highest-leverage cost control in Azure Backup. GRS doubles the storage cost compared to LRS. RA-GRS costs ~2.5×. Yet GRS is often enabled by default in ARM templates and Terraform modules without explicit intent.
          </p>
          <div className="space-y-3">
            {[
              { label: 'LRS — Locally Redundant Storage', cost: '£0.0211/GB/month', rec: 'Dev/test, non-critical workloads, or any backup where regional failure is an acceptable risk. Three copies within a single datacenter.', use: true },
              { label: 'ZRS — Zone Redundant Storage', cost: '£0.0264/GB/month', rec: 'Production workloads that need resilience against datacenter failure within a region. 25% more than LRS.', use: true },
              { label: 'GRS — Geo-Redundant Storage', cost: '£0.0423/GB/month', rec: 'Production-critical workloads with a documented requirement to survive a regional outage. ~2× LRS cost. Do not use for dev/test.', use: false },
              { label: 'RA-GRS — Read-Access Geo-Redundant', cost: '£0.0537/GB/month', rec: 'Same as GRS but with read access to the secondary region, enabling Cross-Region Restore. ~2.5× LRS cost. Only justified when CRR is an explicit DR requirement.', use: false },
            ].map(item => (
              <div key={item.label} className="p-3 rounded text-[13px]" style={{ border: `1px solid ${item.use ? 'var(--outline-variant)' : 'rgba(232,176,0,0.3)'}`, backgroundColor: item.use ? 'var(--bg-surface)' : 'rgba(232,176,0,0.04)' }}>
                <div className="flex justify-between items-start mb-1 gap-2">
                  <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{item.label}</p>
                  <span className="text-[12px] font-mono shrink-0" style={{ color: '#0078d4' }}>{item.cost}</span>
                </div>
                <p style={{ color: 'var(--text-secondary)' }}>{item.rec}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 4 */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            4. The Cross-Region Restore surprise
          </h2>
          <p className="text-[14px] leading-relaxed mb-3" style={{ color: 'var(--text-secondary)' }}>
            Cross-Region Restore (CRR) allows you to restore backed-up data to the paired Azure region — useful if you need to recover from a full regional outage. CRR requires an RA-GRS vault. The cost has two layers that teams often miss:
          </p>
          <ol className="list-decimal pl-5 space-y-2 text-[14px]" style={{ color: 'var(--text-secondary)' }}>
            <li>The RA-GRS storage premium (~2.5× LRS) applies to your entire vault, not just the data you intend to restore across regions.</li>
            <li>When you actually perform a cross-region restore, you pay for the data transfer out of the secondary region. This is a one-time charge per restore event and scales with the data size restored.</li>
          </ol>
          <p className="text-[14px] leading-relaxed mt-3" style={{ color: 'var(--text-secondary)' }}>
            Most teams enable RA-GRS "because it sounds like the safe option" and then never perform a cross-region restore. The ongoing storage premium is a permanent cost for a capability that may never be exercised.
          </p>
        </section>

        {/* Section 5 */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            5. Retention policy and vault storage — the multiplier effect
          </h2>
          <p className="text-[14px] leading-relaxed mb-3" style={{ color: 'var(--text-secondary)' }}>
            Azure Backup's pricing model accumulates storage for each retained backup point. The longer you keep backup history, the more vault storage you consume. The formula is straightforward:
          </p>
          <div className="p-3 rounded text-[13px] font-mono mb-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)', color: 'var(--text-primary)' }}>
            Vault GB ≈ activeDataGb × (1 + dailyChangeRate × retentionDays)
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" style={{ border: '1px solid var(--outline-variant)' }}>
              <thead style={{ backgroundColor: 'var(--surface-container-low)' }}>
                <tr>
                  <th className="text-left px-3 py-2 font-semibold" style={{ color: 'var(--text-primary)' }}>Retention</th>
                  <th className="text-right px-3 py-2 font-semibold" style={{ color: 'var(--text-primary)' }}>VM (5%/day)</th>
                  <th className="text-right px-3 py-2 font-semibold" style={{ color: 'var(--text-primary)' }}>SQL (10%/day)</th>
                  <th className="text-right px-3 py-2 font-semibold" style={{ color: 'var(--text-primary)' }}>Files (2%/day)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['7 days',   '1.35×', '1.7×',  '1.14×'],
                  ['30 days',  '2.5×',  '4.0×',  '1.6×'],
                  ['90 days',  '5.5×',  '10×',   '2.8×'],
                  ['365 days', '19.25×','37.5×',  '8.3×'],
                ].map(([ret, vm, sql, files]) => (
                  <tr key={ret} style={{ borderTop: '1px solid var(--outline-variant)' }}>
                    <td className="px-3 py-2" style={{ color: 'var(--text-secondary)' }}>{ret}</td>
                    <td className="px-3 py-2 text-right" style={{ color: 'var(--text-primary)' }}>{vm}</td>
                    <td className="px-3 py-2 text-right" style={{ color: 'var(--text-primary)' }}>{sql}</td>
                    <td className="px-3 py-2 text-right" style={{ color: 'var(--text-primary)' }}>{files}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[12px] mt-2" style={{ color: 'var(--text-tertiary)' }}>
            Multiplier = vault storage / active data size. A 12-month retention policy for SQL Server stores ~37× the active data size in the vault.
          </p>
        </section>

        {/* Section 6 */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            6. Azure Backup vs Azure Site Recovery — related but not interchangeable
          </h2>
          <p className="text-[14px] leading-relaxed mb-3" style={{ color: 'var(--text-secondary)' }}>
            Both services protect data and both have a storage-dominated cost structure. The use cases are different:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[13px]">
            <div className="p-3 rounded" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)' }}>
              <p className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Azure Backup</p>
              <p style={{ color: 'var(--text-secondary)' }}>Point-in-time restore of data. Protects against accidental deletion, corruption, ransomware. Recovery takes minutes to hours. Instance fee + vault storage.</p>
            </div>
            <div className="p-3 rounded" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)' }}>
              <p className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Azure Site Recovery</p>
              <p style={{ color: 'var(--text-secondary)' }}>Continuous replication + automated failover. Protects against regional infrastructure failure. Sub-minute RPO, low RTO. Instance fee + replica storage + replication transfer + failover compute.</p>
            </div>
          </div>
          <p className="text-[14px] leading-relaxed mt-3" style={{ color: 'var(--text-secondary)' }}>
            Site Recovery is more expensive because it provides continuous replication rather than periodic snapshots. For most workloads, the right architecture uses both: Backup for operational recovery (file restores, corruption) and Site Recovery for DR failover (regional outage). Building a DR business case on Backup alone will leave gaps.
          </p>
        </section>

        {/* Section 7 */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            7. Soft-delete and the storage tail beyond your retention policy
          </h2>
          <p className="text-[14px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            Soft-delete is enabled by default on Azure Recovery Services vaults since 2021. When backup data is deleted (manually or when a retention policy expires), soft-delete keeps the data for 14 additional days before permanent deletion. This adds a storage cost tail beyond your intended policy. For large vaults with high turnover — environments that frequently onboard and offboard VMs — the soft-delete tail can represent a meaningful fraction of the total vault size. It is worth knowing about when building detailed forecasts, though for most workloads it is a second-order effect.
          </p>
        </section>

        {/* Section 8 */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            8. Azure Files backup — the lowest-cost workload type
          </h2>
          <p className="text-[14px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            Azure Files backup has the lowest daily change rate assumption (2% per day) and the lowest instance fee (£4.72/month). For file share workloads that are largely static — document libraries, configuration stores, data exports — the vault storage stays manageable even with longer retention. A 10 TB Azure Files share with 90-day retention accumulates roughly 28 TB in the vault at LRS rates (£591/month total), compared to a similar SQL database which would accumulate ~100 TB. If you have a choice about where to store data that needs long-term backup, Azure Files is the cheaper backup substrate.
          </p>
        </section>

        {/* FAQ */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>FAQ</h2>
          <div className="space-y-4">
            {[
              {
                q: 'Is Azure Backup pricing available in GBP from the Azure Retail Prices API?',
                a: 'No. Azure Backup is USD-only in the Retail Prices API (currencyCode returns USD regardless of region). GBP estimates use a 0.7548 conversion rate (verified April 2026). Check the official pricing page to confirm current GBP rates.'
              },
              {
                q: 'Does Azure Backup charge per GB of active data, or per GB of vault storage?',
                a: 'Per GB of vault storage. Vault storage is larger than active data because it accumulates all retained backup points. A 100 GB volume with 30-day retention and 5% daily change rate occupies roughly 250 GB of vault, not 100 GB.'
              },
              {
                q: 'What is the cheapest way to run Azure Backup for dev/test VMs?',
                a: 'Use LRS vault storage, set retention to the minimum needed (7 days is the minimum), and consider whether full VM backup is needed or whether application-level backup (e.g. database export) is sufficient. LRS + 7-day retention keeps vault storage close to active data size.'
              },
              {
                q: 'Can I use Azure Backup instead of Azure Site Recovery for disaster recovery?',
                a: 'They serve different purposes. Backup provides point-in-time restore — suitable for accidental deletion or corruption recovery. Site Recovery provides continuous replication for near-zero-RPO failover. Using Backup as a DR strategy means accepting hours of data loss (the gap since the last backup) and manual recovery steps.'
              },
              {
                q: 'Are there size tiers in Azure Backup instance fees?',
                a: 'No — as of April 2026, the Retail Prices API shows flat per-instance fees regardless of VM size. Earlier Azure documentation referenced size tiers, but current pricing is flat. Verify on the official pricing page for any changes.'
              },
            ].map(({ q, a }) => (
              <div key={q}>
                <p className="text-[14px] font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{q}</p>
                <p className="text-[14px]" style={{ color: 'var(--text-secondary)' }}>{a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Cross-links */}
        <div className="space-y-3 pt-6" style={{ borderTop: '1px solid var(--outline-variant)' }}>
          <p className="text-[13px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--text-tertiary)' }}>Related calculators and guides</p>
          <Link to="/azure-backup" className="flex items-center gap-3 p-4 rounded group transition-colors" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <span className="material-symbols-outlined text-[#0078d4] text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>backup</span>
            <div>
              <p className="text-[14px] font-semibold group-hover:text-[#0078d4] transition-colors" style={{ color: 'var(--text-secondary)' }}>Azure Backup Calculator</p>
              <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Estimate your backup costs with instance breakdown and storage dominance indicator</p>
            </div>
            <span className="material-symbols-outlined text-[#0078d4] text-[16px] ml-auto">arrow_forward</span>
          </Link>
          <Link to="/site-recovery" className="flex items-center gap-3 p-4 rounded group transition-colors" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <span className="material-symbols-outlined text-[#0078d4] text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>cloud_sync</span>
            <div>
              <p className="text-[14px] font-semibold group-hover:text-[#0078d4] transition-colors" style={{ color: 'var(--text-secondary)' }}>Azure Site Recovery Calculator</p>
              <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Continuous replication for DR — same storage-dominates cost shape as Backup</p>
            </div>
            <span className="material-symbols-outlined text-[#0078d4] text-[16px] ml-auto">arrow_forward</span>
          </Link>
          <Link to="/guides/azure-site-recovery-pricing-uk" className="flex items-center gap-3 p-4 rounded group transition-colors" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <span className="material-symbols-outlined text-[#0078d4] text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>menu_book</span>
            <div>
              <p className="text-[14px] font-semibold group-hover:text-[#0078d4] transition-colors" style={{ color: 'var(--text-secondary)' }}>Azure Site Recovery Pricing UK</p>
              <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>The full DR cost picture — why the per-instance fee is only part of the bill</p>
            </div>
            <span className="material-symbols-outlined text-[#0078d4] text-[16px] ml-auto">arrow_forward</span>
          </Link>
          <Link to="/managed-disks" className="flex items-center gap-3 p-4 rounded group transition-colors" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <span className="material-symbols-outlined text-[#0078d4] text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>storage</span>
            <div>
              <p className="text-[14px] font-semibold group-hover:text-[#0078d4] transition-colors" style={{ color: 'var(--text-secondary)' }}>Managed Disks Calculator</p>
              <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Estimate the disk storage your VMs are running on — plan backup costs alongside disk costs</p>
            </div>
            <span className="material-symbols-outlined text-[#0078d4] text-[16px] ml-auto">arrow_forward</span>
          </Link>
        </div>
      </div>
    </>
  )
}
